import React from 'react';
import { chartColor } from '../chartTheme';

/** One heatmap cell. A null text leaves the cell blank (for example a correlation below n = 10). */
export interface HeatCell {
  key: string;
  text: string | null;
  /** Token-based fill from `heatFill` or `divergingFill`. */
  fill: string;
  /** Full row and column names, shown on hover and read by screen readers. */
  label: string;
  /** Exact values, shown on hover beneath the label. */
  detail?: string;
}

/** Props for {@link Heatmap}. */
export interface HeatmapProps {
  rows: Array<{ key: string; label: string }>;
  columns: Array<{ key: string; label: string }>;
  cells: HeatCell[][];
  onSelect?: (rowIndex: number, columnIndex: number) => void;
  /** Colour scale ends; the one legend a heatmap needs. */
  scale: { low: string; high: string; lowFill: string; highFill: string };
}

/**
 * Grid of cells that print their value on a token fill, with a colour-scale
 * legend. Each cell is a keyboard-focusable mark.
 */
export const Heatmap: React.FC<HeatmapProps> = ({ rows, columns, cells, onSelect, scale }) => (
  <div data-chart="heatmap" className="flex flex-col gap-2 overflow-x-auto">
    <div className="grid gap-px text-xs" style={{ gridTemplateColumns: `minmax(6rem, 10rem) repeat(${columns.length}, minmax(2.75rem, 1fr))` }}>
      <span />
      {columns.map(column => (
        <span key={column.key} className="truncate px-1 pb-1 text-center text-muted-foreground" data-tip={column.label} data-tip-truncated>{column.label}</span>
      ))}
      {rows.map((row, rowIndex) => (
        <React.Fragment key={row.key}>
          <span className="truncate pr-2 leading-8 text-muted-foreground" data-tip={row.label} data-tip-truncated>{row.label}</span>
          {cells[rowIndex].map((cell, columnIndex) => {
            const className = 'h-8 rounded-sm tabular-nums focus-visible:outline-2 focus-visible:outline-ring';
            const style = { background: cell.fill, color: chartColor.text };
            const name = cell.detail ? `${cell.label}: ${cell.detail}` : cell.label;
            return onSelect && cell.text !== null ? (
              <button key={cell.key} type="button" data-mark aria-label={name} data-tip={cell.label} data-tip-detail={cell.detail} className={`${className} cursor-pointer`} style={style} onClick={() => onSelect(rowIndex, columnIndex)}>
                {cell.text}
              </button>
            ) : (
              <span key={cell.key} role="img" data-mark aria-label={name} data-tip={cell.label} data-tip-detail={cell.detail} tabIndex={0} className={`${className} flex items-center justify-center`} style={style}>{cell.text}</span>
            );
          })}
        </React.Fragment>
      ))}
    </div>
    <div data-legend className="flex items-center gap-2 self-end text-xs text-muted-foreground">
      <span className="tabular-nums">{scale.low}</span>
      <span aria-hidden="true" className="h-2 w-20 rounded-sm" style={{ background: `linear-gradient(to right, ${scale.lowFill}, ${scale.highFill})` }} />
      <span className="tabular-nums">{scale.high}</span>
    </div>
  </div>
);
